<template>
  <div class="widget-accordion-custom">
    <div v-if="showAccordion" class="widget-accordion-content">
      <details v-for="(item, index) in items" :key="index" class="widget-accordion-item" :open="openFirst && index===0" :style="item.itemStyle || null">
        <summary class="widget-accordion-summary" :style="item.headerStyle || null">
          <div class="widget-accordion-header">
            <div v-html="item.title"></div>
            <b-badge v-if="item.badge" variant="primary">{{ item.badge }}</b-badge>
          </div>
        </summary>
        <div class="widget-accordion-body" v-if="item.body" v-html="item.body" :style="item.bodyStyle || null"></div>
      </details>
      <div class="widget-accordion-footer" v-if="data.footer">{{ data.footer }}</div>
    </div>
    <WidgetEmptyState v-else :message="emptyMessage" :variant="emptyVariant" />
  </div>
</template>

<script>
  import WidgetEmptyState from './WidgetEmptyState'

  export default {
    components: { WidgetEmptyState },
    props: {
      data: {
        type: Object,
        required: true
      },
    },
    data(){
      return {
        items: [],
        showAccordion: false,
      }
    },
    mounted() {
      this.configureData()
    },
    watch: {
      data: {
        handler: function(){
          this.configureData()
        },
        deep: true,
      },
    },
    computed: {
      // data.open_first: false para que todos los items arranquen cerrados
      openFirst() {
        return this.data.open_first !== false
      },
      emptyVariant() {
        return this.data.empty_variant || this.data.variant || 'info'
      },
      emptyMessage() {
        if(typeof this.data.items === 'string') return this.data.items
        if(Array.isArray(this.data.items) && this.data.items.length === 0) {
          return 'No hay registros para mostrar.'
        }
        return 'No hay datos para mostrar.'
      },
    },
    methods: {
      configureData() {
        if(!Array.isArray(this.data.items) || this.data.items.length === 0) {
          this.showAccordion = false
          this.items = []
        } else {
          this.showAccordion = true
          this.items = this.data.items
        }
      },
    }
  }
</script>

<style>
  .widget-accordion-custom {
    height: 100%;
    min-height: 0;
    overflow: visible;
  }
  .widget-accordion-content {
    display: grid;
    gap: 8px;
    padding: 0;
  }
  .widget-accordion-item {
    overflow: hidden;
    margin: 0;
    border: 1px solid var(--app-color-border);
    border-radius: 9px;
    background: var(--app-color-surface);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .widget-accordion-item[open] {
    border-color: var(--app-color-primary-200);
    box-shadow: var(--app-shadow-sm);
  }
  .widget-accordion-summary {
    display: block;
    list-style: none;
    cursor: pointer;
    padding: 10px 12px;
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-medium);
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  .widget-accordion-summary:hover,
  .widget-accordion-summary:focus {
    outline: none;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
  }
  .widget-accordion-item[open] .widget-accordion-summary {
    border-bottom: 1px solid var(--app-color-border);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
  }
  .widget-accordion-summary::-webkit-details-marker {
    display: none;
  }
  .widget-accordion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }
  .widget-accordion-header > div:first-child { flex: 1 1 auto; min-width: 0; }
  .widget-accordion-footer {
    padding: 8px 12px;
    border: 1px solid var(--app-color-primary-200);
    border-radius: 9px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
    font-size: var(--app-text-base);
    font-weight: var(--app-font-semibold);
    text-align: right;
  }
  /* Título con datos a la derecha (ej.: subtotal) y flecha que gira al abrir */
  .widget-accordion-split { align-items: center; display: flex; gap: 10px; justify-content: space-between; }
  .widget-accordion-split__amount { color: var(--app-color-text); font-size: var(--app-text-base); font-weight: var(--app-font-semibold); text-align: right; white-space: nowrap; }
  .widget-accordion-chevron { color: var(--app-color-text-secondary); font-size: 18px; transition: transform 0.15s ease; }
  .widget-accordion-item[open] .widget-accordion-chevron { transform: rotate(90deg); }
  .widget-group { align-items: center; display: flex; font-size: var(--app-text-xs); font-weight: var(--app-font-bold); gap: 6px; letter-spacing: 0.03em; text-transform: uppercase; }
  .widget-group__icon { align-items: center; border-radius: 6px; display: inline-flex; font-size: 16px; height: 24px; justify-content: center; width: 24px; }
  .widget-group small { color: var(--app-color-text-secondary); font-weight: var(--app-font-medium); }
  .widget-group--success { color: var(--app-color-success); }
  .widget-group--success .widget-group__icon { background: var(--app-color-success-soft); }
  .widget-group--primary { color: var(--app-color-primary-active); }
  .widget-group--primary .widget-group__icon { background: var(--app-color-primary-soft); }
  .widget-group--warning { color: var(--app-color-warning-hover); }
  .widget-group--warning .widget-group__icon { background: var(--app-color-warning-soft); }
  .widget-group--neutral { color: var(--app-color-text-secondary); }
  .widget-group--neutral .widget-group__icon { background: var(--app-color-surface-subtle); }
  /* Tabla de detalle dentro del cuerpo */
  .widget-accordion-table { margin: 0; width: 100%; }
  .widget-accordion-table td { border-top: 1px dashed var(--app-color-border); color: var(--app-color-text); padding: 5px 2px; }
  .widget-accordion-table tr:first-child td { border-top: 0; }
  .widget-accordion-table td.widget-accordion-table__muted { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); }
  .widget-accordion-body {
    padding: 10px 12px;
    color: var(--app-color-text-secondary);
    background: var(--app-color-surface-subtle);
    font-size: var(--app-text-sm);
    line-height: var(--app-leading-default);
  }
</style>
